import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache so the strip never flashes empty between page loads.
let _settingsCache = null;

/**
 * HeaderTrustStrip — the three boutique value-props rendered in the middle
 * of the desktop header, between the MegaMenu and Your Custom Studio CTA.
 *
 * Pieces:
 *   1. Hand-crafted        (thread + needle SVG)
 *   2. Stitched-to-fit     (tape measure SVG)
 *   3. Free shipping ₹X+   (delicate package SVG, threshold from settings)
 *
 * All icons are hand-drawn 18×18 SVGs in 1.25-stroke rosegold to match the
 * boutique theme — deliberately thinner than lucide's defaults so they read
 * "couture" rather than "SaaS dashboard". Hidden on mobile (mobile already
 * has a dense top bar with drawer + logo + CTAs + cart + avatar).
 */
export default function HeaderTrustStrip({ compact = false }) {
    const [freeThreshold, setFreeThreshold] = useState(
        _settingsCache?.free_delivery_threshold ?? null,
    );

    useEffect(() => {
        if (_settingsCache) return;
        axios.get(`${API}/settings/public`).then(r => {
            _settingsCache = r.data || {};
            const t = Number(_settingsCache.free_delivery_threshold) || 0;
            if (t > 0) setFreeThreshold(t);
        }).catch(() => { /* leave label generic */ });
    }, []);

    const formattedThreshold = freeThreshold && freeThreshold > 0
        ? `₹${Number(freeThreshold).toLocaleString("en-IN", { maximumFractionDigits: 0 })}+`
        : null;

    return (
        <nav
            aria-label="Boutique promises"
            data-testid="header-trust-strip"
            aria-hidden={compact ? "true" : undefined}
            className={`cs-trust-strip hidden xl:flex items-center justify-center gap-7 xl:gap-9 ml-6 xl:ml-8 mr-4 flex-1 min-w-0 transition-all duration-300 ease-out ${
                compact ? "opacity-0 -translate-y-1 pointer-events-none" : "opacity-100 translate-y-0"
            }`}
        >
            <TrustPill icon={<HandcraftedGlyph />} label="Hand-crafted" />
            <Divider />
            <TrustPill icon={<TapeMeasureGlyph />} label="Stitched-to-fit" />
            <Divider />
            <TrustPill
                icon={<ParcelGlyph />}
                label={formattedThreshold ? `Free shipping ${formattedThreshold}` : "Free shipping"}
            />
        </nav>
    );
}

const TrustPill = ({ icon, label }) => (
    <div
        className="flex items-center gap-2 text-ink-soft/85 whitespace-nowrap cursor-default select-none"
        data-testid={`header-trust-${label.toLowerCase().split(" ")[0].replace(/[^a-z]/g, "")}`}
    >
        <span className="text-rosegold/80">{icon}</span>
        <span className="font-display text-[14px] italic leading-none tracking-normal">{label}</span>
    </div>
);

const Divider = () => (
    <span aria-hidden="true" className="w-px h-3 bg-rosegold/30 shrink-0" />
);

// --- Hand-drawn elegant SVG glyphs (thin 1.25 stroke, rosegold via currentColor) ---

// Hand-crafted: thread spool with a single curling thread
const HandcraftedGlyph = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {/* Spool body */}
        <rect x="6" y="5" width="12" height="14" rx="1.5" />
        <line x1="6" y1="8" x2="18" y2="8" />
        <line x1="6" y1="16" x2="18" y2="16" />
        {/* Thread coiling */}
        <line x1="9" y1="10" x2="15" y2="10" opacity="0.55" />
        <line x1="9" y1="12" x2="15" y2="12" opacity="0.55" />
        <line x1="9" y1="14" x2="15" y2="14" opacity="0.55" />
        {/* Loose thread + needle */}
        <path d="M18 8 C 21 10, 21 13, 19 14.5" opacity="0.6" />
    </svg>
);

// Stitched-to-fit: tape measure with subtle tick marks
const TapeMeasureGlyph = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {/* Measuring ribbon body */}
        <path d="M3 9 C 3 7, 5 7, 7 7 L 17 7 C 19 7, 21 7, 21 9 L 21 12 C 21 14, 19 15, 17 15 L 7 15 C 5 15, 3 14, 3 12 Z" />
        {/* Tick marks */}
        <line x1="6.5" y1="11" x2="6.5" y2="13" opacity="0.7" />
        <line x1="9" y1="11" x2="9" y2="12.5" opacity="0.55" />
        <line x1="11.5" y1="11" x2="11.5" y2="13" opacity="0.7" />
        <line x1="14" y1="11" x2="14" y2="12.5" opacity="0.55" />
        <line x1="16.5" y1="11" x2="16.5" y2="13" opacity="0.7" />
    </svg>
);

// Free shipping: light parcel with a delicate ribbon tie
const ParcelGlyph = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {/* Parcel body */}
        <path d="M5 8 L 12 4 L 19 8 L 19 16 L 12 20 L 5 16 Z" />
        {/* Front edge fold */}
        <path d="M5 8 L 12 12 L 19 8" />
        <line x1="12" y1="12" x2="12" y2="20" />
        {/* Ribbon tie */}
        <path d="M12 4 L 12 12" opacity="0.55" />
        <path d="M9 6.7 C 10 5.5, 14 5.5, 15 6.7" opacity="0.7" />
    </svg>
);
